import classNames from 'classnames'
import { FunctionComponent, useCallback, useId, useState } from 'react'
import { FOCUS_VISIBLE_OUTLINE_INSET } from '../../../lib/a11yFocus'
import { TabOrientation, useTabList } from './useTabList'

export type TabMenuItem = {
	id: string
	label: string
	panel?: (active: boolean) => React.ReactNode
}

type ITabMenu = {
	tabs: TabMenuItem[]
	ariaLabel: string
	orientation?: TabOrientation
	value?: string
	defaultValue?: string
	onChange?: (id: string, index: number) => void
	idPrefix?: string
	additionalClass?: string
	additionalTabClass?: string
	additionalPanelClass?: string
	testId?: string
}

const TabMenu: FunctionComponent<ITabMenu> = ({
	tabs,
	ariaLabel,
	orientation = 'horizontal',
	value,
	defaultValue,
	onChange,
	idPrefix,
	additionalClass,
	additionalTabClass,
	additionalPanelClass,
	testId,
}) => {
	const generatedIdPrefix = useId()
	const resolvedIdPrefix = idPrefix ?? generatedIdPrefix

	const [uncontrolledValue, setUncontrolledValue] = useState<string | undefined>(
		defaultValue ?? tabs[0]?.id
	)
	const isControlled = value !== undefined
	const activeValue = isControlled ? value : uncontrolledValue
	const activeIndex = tabs.findIndex((tab) => tab.id === activeValue)

	const handleActivate = useCallback(
		(index: number) => {
			const tab = tabs[index]
			if (!tab) return
			if (!isControlled) {
				setUncontrolledValue(tab.id)
			}
			onChange?.(tab.id, index)
		},
		[tabs, isControlled, onChange]
	)

	const { getTabProps, getTabPanelProps, tabRefs } = useTabList({
		tabs,
		activeIndex,
		onActivate: handleActivate,
		orientation,
		idPrefix: resolvedIdPrefix,
	})

	const hasPanels = tabs.some((tab) => tab.panel)
	const isVertical = orientation === 'vertical'

	return (
		<div
			data-testid={testId}
			className={classNames(isVertical && 'flex', additionalClass)}
		>
			<div
				role='tablist'
				aria-label={ariaLabel}
				aria-orientation={isVertical ? 'vertical' : undefined}
				className={classNames('flex', isVertical ? 'flex-col w-1/6 md:w-1/5' : '-mb-px')}
			>
				{tabs.map((tab, index) => {
					const isActive = index === activeIndex
					return (
						<button
							key={tab.id}
							ref={(el) => {
								if (tabRefs.current) {
									tabRefs.current[index] = el
								}
							}}
							type='button'
							className={classNames(
								isVertical
									? 'w-full text-sm text-left px-4 py-3 focus:outline-none'
									: 'flex-1 basis-0 py-4 px-1 text-center text-sm',
								'border-b-2',
								FOCUS_VISIBLE_OUTLINE_INSET,
								isActive
									? 'border-algae-200 text-white'
									: 'border-transparent text-charcoal-200 hover:text-white',
								additionalTabClass
							)}
							{...getTabProps(index)}
							aria-controls={hasPanels ? getTabPanelProps(index).id : undefined}
							onClick={() => handleActivate(index)}
						>
							{tab.label}
						</button>
					)
				})}
			</div>
			{hasPanels && (
				<div className={classNames(isVertical && 'w-full')}>
					{tabs.map((tab, index) => (
						<div key={tab.id} className={additionalPanelClass} {...getTabPanelProps(index)}>
							{tab.panel?.(index === activeIndex)}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default TabMenu
